import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  IconButton,
  OutlinedInput,
  Alert,
} from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { useQuery } from '@tanstack/react-query';
import { teacherService } from 'src/services/teacher.service';
import { CohortWithDetails } from 'src/types/cohort.types';

interface AssignTeachersModalProps {
  open: boolean;
  onClose: () => void;
  cohort: CohortWithDetails | null;
  onConfirm: (cohortId: string, teacherIds: string[]) => Promise<void>;
  isSubmitting?: boolean;
}

export const AssignTeachersModal: React.FC<AssignTeachersModalProps> = ({
  open,
  onClose,
  cohort,
  onConfirm,
  isSubmitting = false,
}) => {
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: teacherData } = useQuery({
    queryKey: ['teachers-all'],
    queryFn: () => teacherService.getTeachers({ page: 1, limit: 100 }),
    enabled: open,
  });

  const teachers = teacherData?.data || [];

  useEffect(() => {
    if (cohort && cohort.teachers) {
      setSelectedTeacherIds(cohort.teachers.map((t) => t.teacherId));
    } else {
      setSelectedTeacherIds([]);
    }
    setError(null);
  }, [cohort, open]);

  if (!cohort) return null;

  const handleSave = async () => {
    try {
      setError(null);
      await onConfirm(cohort.id, selectedTeacherIds);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to update teacher assignments.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Assign Teachers to Cohort: {cohort.name}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Multi-select teachers (SPED case managers, subject teachers, co-teachers)
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <IoClose />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Alert severity="info" sx={{ mb: 2.5, fontSize: '0.8125rem' }}>
          Assigning teachers to <strong>{cohort.name}</strong> gives them permission to manage marketplace payments, classroom charges, and view scholar balances for students in this cohort section.
        </Alert>

        <FormControl fullWidth size="small">
          <InputLabel id="assign-teachers-select-label">Assigned Teachers</InputLabel>
          <Select
            labelId="assign-teachers-select-label"
            multiple
            value={selectedTeacherIds}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedTeacherIds(typeof val === 'string' ? val.split(',') : val);
            }}
            input={<OutlinedInput label="Assigned Teachers" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((teacherId) => {
                  const t = teachers.find((t) => t.id === teacherId);
                  return (
                    <Chip
                      key={teacherId}
                      label={t ? `${t.user?.firstname} ${t.user?.lastname}` : teacherId}
                      size="small"
                      sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600 }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {teachers.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.user?.firstname} {t.user?.lastname} ({t.user?.email}) {t.subject ? `- ${t.subject}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            backgroundColor: '#EDB42E',
            color: '#000',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#d9a224' },
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save Assignments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
